import {
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { ItemImageArea, SkeletonText } from 'utoniq-ui'

import { ConsolePreSaleItemSelectBoxFragment } from '@/clients/utoniq-core/schema'
import { ItemIcon, ItemStatusCell } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

export type ItemSelectBoxProps = {
  checked: boolean
  item: ConsolePreSaleItemSelectBoxFragment
  onSelect: (itemId: string) => void
}

export const ItemSelectBox: Organism<ItemSelectBoxProps> = ({
  checked,
  item,
  onSelect,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: checked ? 'primary.main' : 'grey.50',
        position: 'relative',
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={4}
          alignItems="center"
          sx={{ position: 'relative' }}
        >
          <Stack
            sx={{
              zIndex: 1,
              marginInline: 'auto',
              pl: 1,
              width: 104,
              position: 'absolute',
              top: 8,
              left: 16,
            }}
          >
            <ItemIcon type={item.type} />
          </Stack>
          <ItemImageArea
            size="small"
            src={item.thumbnail.front?.smallUri ?? ''}
          />
          <Stack flex={1} spacing={1}>
            <ItemStatusCell
              isPremium={item.isPremium}
              isPublished={item.isPublished}
              salesCondition={item.salesCondition}
              isDrm={item.isDrm}
            />
            <Typography variant="subtitle1" sx={styleSets.lineCamp(3)}>
              {item.title}
            </Typography>
            {item.sku?.unitName && (
              <Typography variant="caption">{item.sku.unitName}</Typography>
            )}
            <Typography variant="subtitle1">
              {t('price', { number: item.formattedPrice ?? 0 })}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
      <CardActionArea
        onClick={() => onSelect(item.id)}
        sx={{ position: 'absolute', zIndex: 2, inset: 0 }}
      />
    </Card>
  )
}

export const ItemSelectBoxSkeleton: Organism = () => (
  <Card elevation={0} sx={{ borderRadius: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={4} alignItems="center">
        <Skeleton variant="rectangular" width={60} height={96} />
        <Stack flex={1} alignSelf="flex-start">
          <SkeletonText variant="medium" width="80%" />
          <SkeletonText variant="medium" width="80%" />
          <Stack sx={{ pt: 1 }}>
            <SkeletonText variant="small" width="40%" />
            <SkeletonText variant="small" width="20%" />
          </Stack>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)
